import StarIcon from '@material-ui/icons/Star'
import { logComponentSelectorAddClicked } from 'amplitude/helpers/componentSelector'
import { ComponentVersions_3_0 } from 'constants/uxVersions'
import { orgSelectors } from 'ducks/orgs'
import { permissionsSelectors } from 'ducks/permissions'
import ComponentOverviewIcon from 'elements/hardwareSelector/ComponentOverview'
import ShowcaseModal from 'expo/showcase/ShowcaseModal'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import {
  Box,
  Chip,
  ComponentVersionsInherit,
  DatasheetIcon,
  Grid,
  IconButton,
  ShowcaseIcon,
  styled,
} from 'opensolar-ui'
import { ComponentCost } from 'pages/ordering/type'
import AccordionCard from 'projectSections/sections/info/AccordionCard'
import { memo, useEffect, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { ComponentTypes } from 'types/selectComponent'
import { ActionType } from 'types/tracking'
import { useFeatureFlag } from 'util/featureFlags'
import { getHardwareProductImageUrlCandidates, shouldShowHardwareProductImage } from '../hardwareProductImage'
import { HardwareComponentType, HardwareListRowProps } from '../types'
import useComponentSelectorColumnWidth from '../useComponentSelectorColumnWidth'
import DistributorBlock from './DistributorBlock'
import DistributorBlockV2 from './DistributorBlockV2'
import DistributorDetails from './DistributorDetails'
import HardwareImage from './HardwareImage'
import ManufacturerBlock from './ManufacturerBlock'
import MultiSelectQuantityControl from './MultiSelectQuantityControl'
import NameBlock from './NameBlock'
import PriceBlock from './PriceBlock'
import QuantityBlock from './QuantityBlock'
import RatingBlock from './RatingBlock'
import SelectButton from './SelectButton'
import { getComponentData } from './utility'

interface StyleProps {
  isExpandable: boolean
}

export const useHardwareListRowStyles = makeOpenSolarStyles((theme) => ({
  container: {
    width: '100%',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    '&:last-child .distributor-popover': {
      top: '-45px',
    },
  },
  accordion: {
    backgroundColor: 'inherit',
  },
  row: {
    boxSizing: 'border-box',
    position: 'relative',
    width: '100%',
  },
  gridContainer: {
    height: '100%',
    padding: '10px',
    alignItems: 'center',
    '& > *': {
      minWidth: '0px',
      padding: '0 8px 0 0',
    },
  },

  starIcon: {
    color: theme.openSolarYellow,
    left: 0,
    position: 'absolute',
    top: 0,
  },
  accordionDetails: {
    background: '#F8F8F8',
    padding: '14px 64px 18px 18px',
  },
  badgeWrapper: {
    marginTop: 5,
  },
}))

const StyledGrid = styled(Grid)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
})

const HardwareListRow: React.FC<HardwareListRowProps<HardwareComponentType>> = ({
  allowCreate,
  record: component,
  onSelect,
  hiddenColumnGroups,
  multiSelectMode = false,
  selectedQuantity = 0,
  onQuantityChange,
}) => {
  const translate = useTranslate()

  const [costBySource, setCostBySource] = useState<ComponentCost | undefined>()

  const enabledMultiDistributorSelector = useFeatureFlag('design_multi_distributor', 'on')
  const orgName = useSelector(orgSelectors.getOrg)?.name
  const currentOrgId = useSelector(orgSelectors.getOrg)?.id

  const enabledDistributors = useSelector(orgSelectors.getEnabledHardwareSuppliers) ?? []

  // this is constantly false since accordion functionality removed for phase 1
  const isExpandable = false && component.ordering_v2.length > 1
  const componentType = component.type
  const componentCode = component.code

  const componentData = useMemo(() => getComponentData(component), [component])

  const shouldShowProductImage = shouldShowHardwareProductImage(component)
  const productImageUrls = getHardwareProductImageUrlCandidates(component)

  // Ordered by priority for image fallback logic
  const logoUrls = [
    component.logo, // logo from showcase content
    component.manufacturer_logo,
  ]

  const classes = useHardwareListRowStyles()
  const hardwareSourceSelectionPermission = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'hardware_source_selection')
  )
  const [isShowcaseModalOpen, setShowcaseModalOpen] = useState(false)
  const expoEnabled = useFeatureFlag('expo', 'on')
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'hardware_list_row',
    eventName: 'Hardware Notification',
  })
  const hardwareSelectorColumnWidth = useComponentSelectorColumnWidth('hardware')

  useEffect(() => {
    if (!hardwareSourceSelectionPermission.allowEdit) {
      const componentActivationCost =
        window.AccountHelper.getComponentActivationFromCode(componentCode, componentType as ComponentTypes)?.cost || 0
      setCostBySource({ code: componentCode, source: 'component', price: componentActivationCost })
    }
  }, [hardwareSourceSelectionPermission])

  const handleSelect = () => {
    logComponentSelectorAddClicked({
      componentTypes: [componentType as ComponentTypes],
      componentCode,
      context: !costBySource?.source ? 'no_source_chip_selected' : 'source_chip_selected',
    })
    onSelect(component, costBySource?.source, costBySource?.price || 0)

    if (enabledMultiDistributorSelector) {
      trackUserInteraction(
        { type: ActionType.SELECT },
        {
          source: 'Design_RHS_BOM',
        }
      )
    }
  }

  const showQuantityControls = multiSelectMode && selectedQuantity >= 1 && onQuantityChange

  const Row = (
    <div className={classes.row}>
      <Grid
        alignItems="flex-start"
        className={classes.gridContainer}
        container
        xs={12}
        style={{ paddingRight: isExpandable ? '60px' : '10px' }}
      >
        {component.is_my_list && !component?.is_archived && component.org_id === currentOrgId && (
          <StarIcon className={classes.starIcon} />
        )}
        <Grid item xs={hardwareSelectorColumnWidth.item}>
          <HardwareImage imageUrls={shouldShowProductImage ? productImageUrls : []} />
        </Grid>
        <StyledGrid item xs={hardwareSelectorColumnWidth.brand}>
          <ManufacturerBlock
            name={component.manufacturer_name}
            logoUrls={logoUrls}
            isExhibitor={component.is_exhibitor}
            distributors={component.ordering_v2}
          />
          {component.is_exhibitor && (
            <div className={classes.badgeWrapper}>
              <ComponentVersionsInherit versions={ComponentVersions_3_0}>
                <Chip label={translate('Showcase Partner')} color="primary" />
              </ComponentVersionsInherit>
            </div>
          )}
          {expoEnabled && (
            <div className={classes.badgeWrapper}>
              <ComponentVersionsInherit versions={ComponentVersions_3_0}>
                <IconButton
                  disableRipple
                  style={{ padding: 0 }}
                  onClick={() => {
                    setShowcaseModalOpen(true)
                  }}
                >
                  {component.partner_expo_stand || (component.is_exhibitor && component.product_details) ? (
                    <ShowcaseIcon width="28" height="28" />
                  ) : (
                    <DatasheetIcon width="28" height="28" />
                  )}
                </IconButton>
                <ShowcaseModal
                  component={component}
                  open={isShowcaseModalOpen}
                  onClose={() => {
                    setShowcaseModalOpen(false)
                  }}
                />
              </ComponentVersionsInherit>
            </div>
          )}
        </StyledGrid>
        <Grid item xs={hardwareSelectorColumnWidth.name}>
          <NameBlock
            name={component.code}
            // @ts-ignore
            helperIcon={<ComponentOverviewIcon component={{ ...(componentData || {}), ...component }} />}
            series={
              component.component_description ||
              component.ordering_v2?.find(({ short_description }) => short_description)?.short_description
            }
            component={component}
          />
        </Grid>
        {!hiddenColumnGroups?.has('rating') && (
          <Grid item xs={hardwareSelectorColumnWidth.rating}>
            <RatingBlock componentData={componentData} componentType={componentType} />{' '}
          </Grid>
        )}
        {Number(hardwareSelectorColumnWidth.source) > 0 && (
          <Grid item xs={hardwareSelectorColumnWidth.source}>
            <Box display="flex" flexDirection="column" alignItems="flex-start" gridGap={12}>
              {enabledMultiDistributorSelector ? (
                <DistributorBlockV2
                  distributors={component.ordering_v2}
                  onUpdateCostBySource={(source, price) =>
                    setCostBySource(!source ? undefined : { code: componentCode, source, price })
                  }
                  component={component}
                />
              ) : (
                <DistributorBlock distributors={component.ordering_v2} />
              )}
              <QuantityBlock
                distributors={component.ordering_v2}
                costSource={costBySource}
                productCode={component.code}
                productName={component.name}
              />
            </Box>
          </Grid>
        )}
        {Number(hardwareSelectorColumnWidth.price) > 0 && (
          <Grid item xs={hardwareSelectorColumnWidth.price}>
            <PriceBlock
              code={component.code}
              componentData={componentData}
              componentType={componentType}
              distributors={component.ordering_v2}
              costSource={costBySource}
            />
          </Grid>
        )}
        <Grid
          item
          xs={hardwareSelectorColumnWidth.action}
          style={multiSelectMode ? { minWidth: 'min-content' } : undefined}
        >
          <Box display="flex">
            {showQuantityControls ? (
              <MultiSelectQuantityControl
                value={selectedQuantity}
                onChange={(newQty) => onQuantityChange?.(component, newQty)}
                disabled={!allowCreate && !component.is_my_list}
              />
            ) : (
              <SelectButton
                enabled={allowCreate || component.is_my_list}
                orgName={orgName}
                handleSelect={handleSelect}
                multiSelect={multiSelectMode}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </div>
  )
  return (
    <div className={classes.container}>
      {isExpandable ? (
        <AccordionCard
          detailsClassName={classes.accordionDetails}
          disableGutters
          elevation={0}
          name={`component-${component.code}`}
          render={() => (
            <DistributorDetails
              code={component.code}
              componentType={componentType}
              // Ignored as isExpandable already ensures min. length of component.ordering_v2 is 2
              // @ts-ignore
              distributors={component.ordering_v2}
            />
          )}
          summaryContent={Row}
        />
      ) : (
        Row
      )}
    </div>
  )
}

export default memo(HardwareListRow)
